import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store/index';
import AdminIndex from '@/components/admin/Index';
import AdminSurveys from '@/components/admin/surveys/Index';
import AdminNewSurvey from '@/components/admin/surveys/New';
import AdminSurveyHeader from '@/components/admin/surveys/Header';
import AdminShowSurvey from '@/components/admin/surveys/Show';
import AdminEditSurvey from '@/components/admin/surveys/Edit';
import AdminDesignSurvey from '@/components/admin/surveys/Design';
import AdminPreviewSurvey from '@/components/admin/surveys/Preview';
import AdminSurveyAnalyse from '@/components/admin/surveys/Analyse';
import AdminSurveyResponse from '@/components/admin/surveys/Response';
import AdminNewPage from '@/components/admin/pages/New';
import AdminEditPage from '@/components/admin/pages/Edit';
import AdminNewField from '@/components/admin/fields/New';
import AdminEditField from '@/components/admin/fields/Edit';
import App from '@/components/App';
import Auth from '@/components/Auth';
import Index from '@/components/Index';
import SurveyNew from '@/components/survey/New';
import SurveyComplete from '@/components/survey/Complete';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      component: App,
      children: [
        {
          path: '',
          name: 'index',
          component: Index,
        },
        {
          path: '/login',
          name: 'login',
          beforeEnter(to) {
            Vue.axios.get('auth').then((response) => {
              let authRedirectUrl = response.data;
              if (process.env.REDIRECT_URL_UID) {
                authRedirectUrl += `&redirect_url_uid=${process.env.REDIRECT_URL_UID}`;
              }
              if (to.query.state) {
                authRedirectUrl += `&state=${to.query.state}`;
              }
              window.location = authRedirectUrl;
            });
          },
        },
        {
          path: '/auth',
          name: 'auth',
          component: Auth,
          props: route => ({ authToken: route.query.auth_token, state: route.query.state }),
        },
        {
          path: '/admin',
          component: AdminIndex,
          children: [
            {
              path: '',
              name: 'admin-index',
              redirect: { name: 'admin-surveys' },
            },
            {
              path: 'surveys',
              name: 'admin-surveys',
              component: AdminSurveys,
            },
            {
              path: 'surveys/new',
              name: 'admin-new-survey',
              component: AdminNewSurvey,
            },
            {
              path: 'surveys/:surveyId',
              component: AdminSurveyHeader,
              children: [
                {
                  path: '',
                  name: 'admin-show-survey',
                  component: AdminShowSurvey,
                },
                {
                  path: 'design',
                  name: 'admin-design-survey',
                  component: AdminDesignSurvey,
                },
                {
                  path: 'preview',
                  name: 'admin-preview-survey',
                  component: AdminPreviewSurvey,
                },
                {
                  path: 'response/analyse',
                  name: 'admin-survey-analyse',
                  component: AdminSurveyAnalyse,
                },
                {
                  path: 'response/:responseId',
                  name: 'admin-survey-response',
                  component: AdminSurveyResponse,
                },
                {
                  path: 'pages/:pageId/edit',
                  name: 'admin-edit-page',
                  component: AdminEditPage,
                },
                {
                  path: 'pages/new',
                  name: 'admin-new-page',
                  component: AdminNewPage,
                },
                {
                  path: 'pages/:pageId/fields/new',
                  name: 'admin-new-field',
                  component: AdminNewField,
                },
                {
                  path: 'pages/:pageId/fields/:fieldId/edit',
                  name: 'admin-edit-field',
                  component: AdminEditField,
                },
              ],
            },
            {
              path: 'surveys/:surveyId/edit',
              name: 'admin-edit-survey',
              component: AdminEditSurvey,
            },
          ],
        },
        {
          path: '/:surveyRef/:surveyInviteId?',
          name: 'survey-new',
          component: SurveyNew,
          props: true,
        },
        {
          path: '/:surveyRef/complete',
          name: 'survey-complete',
          component: SurveyComplete,
        },
        {
          path: '/:surveyRef/:surveyInviteId?/response/:surveyId/page/:pageId',
          name: 'survey-resume',
          component: SurveyNew,
          props: true,
        },
      ],
    },
  ],
});

const requiresLogin = (to) => {
  return !store.state.user.activeUser.id
    && ['login', 'auth', 'survey-new', 'survey-resume', 'survey-complete'].indexOf(to.name) === -1;
};

router.beforeEach((to, from, next) => {
  window.scrollTo(0, 0);

  const sessionId = localStorage.getItem('surveySessionId');
  if (sessionId && !store.state.user.activeUser.id) {
    store.commit('setWillAuthenticate', true);
    store.dispatch('loginUser', { sessionId });
  } else if (requiresLogin(to)) {
    return next({ name: 'login', query: { state: to.fullPath } });
  }

  const resources = [
    { name: 'Admin', adminOnly: true },
    { key: 'surveyId', name: 'Survey', adminOnly: true },
    { key: 'surveyRef', name: 'Survey', userOnly: true, noActiveResource: true },
    { key: 'surveyRef', name: 'Survey', userOnly: true, singleResource: true },
    { key: 'responseId', dependsOn: 'surveyId', name: 'Response', adminOnly: true },
    { key: 'pageId', dependsOn: 'surveyId', name: 'Page', adminOnly: true },
    { key: 'fieldId', dependsOn: 'pageId', name: 'Field', adminOnly: true },
    { key: 'fieldOptionId', name: 'FieldOption', dependsOn: 'fieldId', adminOnly: true, noActiveResource: true },
  ];

  resources.forEach((resource) => {
    store.dispatch('loadResources', { to, resource });
  });

  return next();
});

export default router;
